import { useState } from 'react';
import { Lock } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';
import { Switch } from '@/components/ui/Switch';
import { WorkMoodPicker } from './WorkMoodPicker';
import { useCheckin } from '@/features/workMode/hooks';
import { describeWorkError } from '@/features/workMode/api';

interface WorkCheckinScreenProps {
  name: string;
  sessionId: string | null;
  onDone: () => void;
}

/** Daily "how do you feel today?" — one tap, optional note, optional privacy. */
export function WorkCheckinScreen({ name, sessionId, onDone }: WorkCheckinScreenProps) {
  const [mood, setMood] = useState<1 | 2 | 3 | 4 | 5 | null>(null);
  const [note, setNote] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const checkin = useCheckin();
  const first = name.split(' ')[0] || name;

  const save = async () => {
    if (!mood) return;
    try {
      await checkin.mutateAsync({ mood, note: note.trim() || null, isPrivate, sessionId });
      toast.success(mood <= 2 ? 'Thanks for being honest. A manager will check in.' : 'Thanks — have a good one.');
      onDone();
    } catch (e) {
      toast.error(describeWorkError(e));
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-1 flex-col justify-center gap-8 px-6 py-10">
      <div className="text-center">
        <p className="text-sm uppercase tracking-wide text-muted-foreground">Daily check-in</p>
        <h1 className="mt-1 text-3xl font-medium tracking-headline text-foreground">How do you feel today, {first}?</h1>
      </div>

      <WorkMoodPicker value={mood} onChange={setMood} />

      <div className="space-y-3">
        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Anything you want to add? (optional)"
          className="min-h-[80px] text-base"
        />
        <label className="flex items-center justify-between gap-4 rounded-xl border border-border bg-card px-4 py-3">
          <span className="flex items-center gap-2 text-sm text-foreground">
            <Lock className="h-4 w-4 text-muted-foreground" />
            Keep my answer private
          </span>
          <Switch checked={isPrivate} onCheckedChange={setIsPrivate} />
        </label>
      </div>

      <div className="flex items-center justify-between gap-3">
        <Button variant="ghost" size="lg" onClick={onDone} disabled={checkin.isPending}>
          Skip for today
        </Button>
        <Button size="lg" onClick={save} disabled={!mood || checkin.isPending} className="min-w-40">
          {checkin.isPending ? 'Saving…' : 'Start working'}
        </Button>
      </div>
    </div>
  );
}
